import { SettingsCard, SettingsGroup, SettingsRow } from './SettingsPrimitives';
import { useState } from 'react';
import { Trash2, Sparkles, Plus, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useDialer } from '@/contexts/DialerContext';
import { useConfirmDialog } from '@/components/shared/ConfirmDialog';
import { errorCodeOf, errorMessage } from '@/services/errorMessage';
import {
  activateSummaryPrompt, createSummaryPrompt, deleteSummaryPrompt, listSummaryPrompts,
  type SummaryPrompt, type SummaryPromptList,
} from '@/api/endpoints/tenant';
import { CoachBasePromptCard, CustomerCoachingSection, TenantCoachingSection } from './CoachingSections';
import { useSettingsSavedToast } from './useSettingsSavedToast';

// ─── Section: IA (W4: IA and Coaching merged) ─────────────────────────────────
//
// One section for everything the AI does on calls, in two parts:
//   - Vos préférences: the live coach switches and the conversation
//     threshold, stored in the caller's dialer settings;
//   - the organisation's instructions: the summary prompts (server, W4) and
//     the live coach prompts (base, organisation, selected client), written
//     by a tenant admin, read by everyone else. The server refuses the
//     writes of a non-admin; the screen does not offer them.
// ?section=coaching opens this section (sections.ts).

const SUMMARY_PROMPTS_KEY = ['ai-summary-prompts'];

/** The translated message of a failed prompt request, then its code (DES-16). */
function promptError(err: unknown, t: (k: string, o?: Record<string, unknown>) => string): [string, { description?: string }] {
  const code = errorCodeOf(err);
  const known = code.startsWith('AI_PROMPT_') ? `settings:aiSummary.errors.${code}` : '';
  const message = known && t(known) !== known ? t(known) : errorMessage(err, t, 'settings:aiSummary.saveError');
  return [message, code ? { description: code } : {}];
}

function promptName(p: SummaryPrompt, t: (k: string) => string): string {
  return p.is_default ? t('settings:aiSummary.defaultPromptName') : p.name;
}

function AiSummaryPrompts() {
  const { t } = useTranslation(['settings', 'common']);
  const queryClient = useQueryClient();
  const [ask, confirmDialog] = useConfirmDialog();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: SUMMARY_PROMPTS_KEY,
    queryFn: ({ signal }) => listSummaryPrompts({ signal }),
    staleTime: 60_000,
  });
  const canEdit = !!data?.can_edit;
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [instructions, setInstructions] = useState('');

  const setList = (list: SummaryPromptList) => queryClient.setQueryData(SUMMARY_PROMPTS_KEY, list);
  const refresh = () => queryClient.invalidateQueries({ queryKey: SUMMARY_PROMPTS_KEY });

  const create = useMutation({
    mutationFn: () => createSummaryPrompt({ name: name.trim(), instructions: instructions.trim() }),
    onSuccess: (p) => {
      toast.success(t('settings:aiSummary.created', { name: p.name }));
      setName(''); setInstructions(''); setOpen(false);
      void refresh();
    },
    onError: (err) => toast.error(...promptError(err, t)),
  });
  const activate = useMutation({
    mutationFn: (id: string) => activateSummaryPrompt(id),
    onSuccess: (list) => { setList(list); toast.success(t('settings:aiSummary.activated'), { duration: 1500 }); },
    // The list may be stale (a prompt deleted elsewhere): reread it.
    onError: (err) => { toast.error(...promptError(err, t)); void refresh(); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteSummaryPrompt(id),
    onSuccess: () => { toast.success(t('settings:aiSummary.deleted')); void refresh(); },
    onError: (err) => { toast.error(...promptError(err, t)); void refresh(); },
  });

  const submit = () => {
    if (!name.trim()) { toast.error(t('settings:aiSummary.needName')); return; }
    if (!instructions.trim()) { toast.error(t('settings:aiSummary.needInstructions')); return; }
    create.mutate();
  };

  const confirmDelete = async (p: SummaryPrompt) => {
    const ok = await ask({
      title: t('settings:aiSummary.deleteConfirmTitle', { name: p.name }),
      description: p.is_active ? t('settings:aiSummary.deleteConfirmActive') : undefined,
      confirmLabel: t('common:actions.delete'),
      tone: 'destructive',
    });
    if (ok) remove.mutate(p.id);
  };

  return (
    <SettingsCard>
      {confirmDialog}
      <div className="px-5 py-4 flex items-center justify-between gap-3 border-b border-border" data-testid="ai-summary-prompts">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">{t('settings:aiSummary.title')}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{t('settings:aiSummary.subtitle')}</p>
        </div>
        {canEdit && (
          <Button size="sm" className="h-9 shrink-0" data-testid="nouveau-prompt" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            {t('settings:aiSummary.newPrompt')}
          </Button>
        )}
      </div>

      {open && canEdit && (
        <div className="px-5 py-4 border-b border-border space-y-3">
          <div>
            <Label htmlFor="prompt-nom" className="text-xs">{t('settings:aiSummary.nameLabel')}</Label>
            <Input
              id="prompt-nom"
              className="h-9"
              data-testid="prompt-nom"
              value={name}
              maxLength={80}
              placeholder={t('settings:aiSummary.namePlaceholder')}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="prompt-instructions" className="text-xs">{t('settings:aiSummary.instructionsLabel')}</Label>
            <textarea
              id="prompt-instructions"
              data-testid="prompt-instructions"
              value={instructions}
              maxLength={4000}
              onChange={(e) => setInstructions(e.target.value)}
              rows={4}
              placeholder={t('settings:aiSummary.instructionsPlaceholder')}
              className="w-full rounded-md border border-border bg-background text-foreground text-sm px-3 py-2 outline-none focus:ring-2 focus:ring-ring resize-y"
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="h-9" onClick={submit} disabled={create.isPending}>
              {create.isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              {t('settings:aiSummary.create')}
            </Button>
            <Button size="sm" variant="outline" className="h-9" onClick={() => setOpen(false)}>{t('common:actions.cancel')}</Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="px-5 py-4 text-sm text-muted-foreground">{t('common:actions.loading')}</p>
      ) : isError ? (
        <div className="px-5 py-4 flex items-center justify-between gap-3" role="alert">
          <p className="text-sm text-destructive">{t('settings:aiSummary.loadError')}</p>
          <Button size="sm" variant="outline" className="h-9" onClick={() => void refetch()}>{t('settings:toasts.retry')}</Button>
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {(data?.prompts || []).map((p) => (
            <li key={p.id} className="px-5 py-3 flex items-start justify-between gap-3" data-testid={`prompt-${p.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-muted-foreground shrink-0" aria-hidden />
                  <span className="text-sm font-medium text-foreground">{promptName(p, t)}</span>
                  {p.is_default && <Badge variant="secondary" className="text-[10px]">{t('settings:aiSummary.default')}</Badge>}
                  {p.is_active && (
                    <Badge className="text-[10px] bg-green-600 hover:bg-green-600" data-testid="prompt-actif">{t('common:states.active')}</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.instructions}</p>
              </div>
              {canEdit && (
                <div className="flex items-center gap-1 shrink-0">
                  {!p.is_active && (
                    <Button size="sm" variant="outline" className="h-8 text-xs" disabled={activate.isPending} onClick={() => activate.mutate(p.id)}>
                      {t('settings:aiSummary.activate')}
                    </Button>
                  )}
                  {/* #83: the default prompt has no delete button (the client
                      asked that it stays); the server refuses it too. */}
                  {!p.is_default && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                      aria-label={t('settings:aiSummary.deleteAria', { name: p.name })}
                      onClick={() => void confirmDelete(p)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {data && !canEdit && (
        <p className="px-5 py-3 border-t border-border text-xs text-muted-foreground" data-testid="ai-readonly-note">
          {t('settings:aiSummary.readOnly')}
        </p>
      )}
    </SettingsCard>
  );
}

export function AISection() {
  const { t } = useTranslation(['settings', 'common']);
  const d = useDialer();
  const {
    conversationThreshold, setConversationThreshold,
    aiCoachEnabledPowerMode, setAiCoachEnabledPowerMode,
    aiCoachEnabledManualCall, setAiCoachEnabledManualCall,
  } = d;

  // These three are the caller's own dialer settings: same server-answer
  // toast as Général (RUI-09).
  useSettingsSavedToast(JSON.stringify({ conversationThreshold, aiCoachEnabledPowerMode, aiCoachEnabledManualCall }));

  return (
    <div className="space-y-8">
      <SettingsGroup title={t('settings:ai.groupMine')}>
        <SettingsCard>
          <SettingsRow label={t('settings:ai.liveCoachPowerMode')}>
            <Switch
              aria-label={t('settings:ai.liveCoachPowerMode')}
              checked={aiCoachEnabledPowerMode !== false}
              onCheckedChange={(v) => setAiCoachEnabledPowerMode(Boolean(v))}
            />
          </SettingsRow>
          <SettingsRow label={t('settings:ai.liveCoachManualCall')}>
            <Switch
              aria-label={t('settings:ai.liveCoachManualCall')}
              checked={aiCoachEnabledManualCall !== false}
              onCheckedChange={(v) => setAiCoachEnabledManualCall(Boolean(v))}
            />
          </SettingsRow>
          <SettingsRow label={t('settings:ai.conversationThresholdLabel')} hint={t('settings:ai.conversationThresholdHint')} last>
            <Select
              value={String(conversationThreshold)}
              onValueChange={(v) => setConversationThreshold(Number(v))}
            >
              <SelectTrigger className="w-36 h-9" aria-label={t('settings:ai.conversationThresholdLabel')}><SelectValue /></SelectTrigger>
              <SelectContent>
                {[15, 20, 30, 45, 60, 90, 120].map((s) => (
                  <SelectItem key={s} value={String(s)}>{t('settings:units.secondsCount', { count: s })}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SettingsRow>
        </SettingsCard>
      </SettingsGroup>

      <SettingsGroup title={t('settings:ai.groupSummary')}>
        <AiSummaryPrompts />
      </SettingsGroup>

      <SettingsGroup title={t('settings:ai.groupCoach')}>
        <CoachBasePromptCard />
        <TenantCoachingSection />
        <CustomerCoachingSection d={d} />
      </SettingsGroup>
    </div>
  );
}
